import { useCallback, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';

import { RootState } from '~/store';

import useAnalytics from '../useAnalytics/useAnalytics';

interface UsageTrackingOptions {
  trackPageViews?: boolean;
  trackIdleTime?: boolean;
  idleTimeoutMs?: number;
}

export const useUsageTracking = (options: UsageTrackingOptions = {}) => {
  const { track } = useAnalytics();

  // Get ecosystem and organization data from Redux store
  const selectedEcosystem = useSelector(
    (state: RootState) => state.ecosystem.selectedEcosystem,
  );
  const selectedUserProfile = useSelector(
    (state: RootState) => state.auth.selectedUserProfile,
  );
  const lastActivityTime = useRef<number>(Date.now());
  const isIdle = useRef<boolean>(false);
  const idleTimeoutId = useRef<NodeJS.Timeout | null>(null);

  const {
    trackPageViews = true,
    trackIdleTime = true,
    idleTimeoutMs = 300000, // 5 minutes default
  } = options;

  // Helper function to get common properties
  const getCommonProperties = useCallback(() => {
    const ecosystem_id =
      selectedEcosystem?._id || selectedUserProfile?.ecosystem;
    const ecosystem_name =
      selectedEcosystem?.name || selectedUserProfile?.ecosystemSummary?.name;
    const org_id = selectedUserProfile?.organization;
    const org_name = (selectedUserProfile?.userOrganization as any)?.name;

    return {
      ecosystem_id,
      ecosystem_name,
      org_id,
      org_name,
    };
  }, [selectedEcosystem, selectedUserProfile]);

  // Track page view
  const trackPageView = useCallback(
    (pageName: string, additionalProperties?: Record<string, unknown>) => {
      if (trackPageViews) {
        const commonProps = getCommonProperties();

        // Remove undefined values
        const cleanProps = Object.fromEntries(
          Object.entries(commonProps).filter(
            ([, value]) =>
              value !== undefined && value !== null && value !== '',
          ),
        );

        track('page_view', {
          page_name: pageName,
          timestamp: Date.now(),
          url: window.location.href,
          referrer: document.referrer,
          ...cleanProps,
          ...additionalProperties,
        });
      }
    },
    [track, trackPageViews, getCommonProperties],
  );

  // Track user activity
  const trackActivity = useCallback(() => {
    lastActivityTime.current = Date.now();

    if (isIdle.current) {
      isIdle.current = false;
      const commonProps = getCommonProperties();

      // Remove undefined values
      const cleanProps = Object.fromEntries(
        Object.entries(commonProps).filter(
          ([, value]) => value !== undefined && value !== null && value !== '',
        ),
      );

      track('user_active', {
        timestamp: Date.now(),
        ...cleanProps,
      });
    }

    // Reset idle timeout
    if (idleTimeoutId.current) {
      clearTimeout(idleTimeoutId.current);
    }

    if (trackIdleTime) {
      idleTimeoutId.current = setTimeout(() => {
        if (!isIdle.current) {
          isIdle.current = true;
          const commonProps = getCommonProperties();

          // Remove undefined values
          const cleanProps = Object.fromEntries(
            Object.entries(commonProps).filter(
              ([, value]) =>
                value !== undefined && value !== null && value !== '',
            ),
          );

          track('user_idle', {
            timestamp: Date.now(),
            idle_timeout_ms: idleTimeoutMs,
            ...cleanProps,
          });
        }
      }, idleTimeoutMs);
    }
  }, [track, trackIdleTime, idleTimeoutMs, getCommonProperties]);

  useEffect(() => {
    // Activity event listeners
    const activityEvents = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click',
    ];

    activityEvents.forEach((event) => {
      document.addEventListener(event, trackActivity, { passive: true });
    });

    // Initial activity tracking
    trackActivity();

    return () => {
      // Clean up event listeners
      activityEvents.forEach((event) => {
        document.removeEventListener(event, trackActivity);
      });

      if (idleTimeoutId.current) {
        clearTimeout(idleTimeoutId.current);
      }
    };
  }, [trackActivity]);

  return {
    trackPageView,
    trackActivity,
    isIdle: isIdle.current,
  };
};

export default useUsageTracking;
